import type React from 'react';
import classes from './costs-page.module.scss';
import { Navigate } from 'react-router-dom';
import { additionalLabels, unitLabels } from './costs-page.utils';
import { useCostsPageController } from './useCostsPageController';

export const CostsPage: React.FC = () => {
  const {
    user,
    data,
    typeCost,
    setTypeCost,
    name,
    setName,
    regularType,
    setRegularType,
    additionalCode,
    setAdditionalCode,
    roleIds,
    setRoleIds,
    valueCostId,
    setValueCostId,
    value,
    setValue,
    valueRoleId,
    setValueRoleId,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    error,
    isSaving,
    isLoadingMore,
    onLoadMoreCosts,
    onLoadMoreValues,
    onCreateCost,
    onAddCostValue,
    selectedCost,
    onChangeShiftTipType,
    onToggleCostActive,
  } = useCostsPageController();

  if (user?.role !== 'manager') return <Navigate to="/" replace />;

  return (
    <section className="workforcePage" dir="rtl">
      <div className="workforceHeader">
        <div>
          <h1>עלויות</h1>
          <p>הגדרות עלות וערכים היסטוריים לחישובי כוח אדם וטיפים</p>
        </div>
      </div>
      {error && <p className="errorText">{error}</p>}
      <section className="workforceBand">
        <h2>עלות חדשה</h2>
        <form className="workforceForm" onSubmit={onCreateCost}>
          <label>
            סוג
            <select value={typeCost} onChange={(event) => setTypeCost(event.target.value as typeof typeCost)}>
              <option value="additional">עלות נוספת</option>
              <option value="regular">עלות רגילה</option>
            </select>
          </label>
          <label>
            שם
            <input required maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          {typeCost === 'additional' ? (
            <label>
              חישוב
              <select
                value={additionalCode}
                onChange={(event) => setAdditionalCode(event.target.value as typeof additionalCode)}
              >
                {Object.entries(additionalLabels).map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label>
              יחידה
              <select
                value={regularType}
                onChange={(event) => setRegularType(event.target.value as typeof regularType)}
              >
                {Object.entries(unitLabels).map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {typeCost === 'regular' && (
            <fieldset className={classes.rolePicker}>
              <legend>תפקידים</legend>
              {data?.roles.map((role) => (
                <label key={role.id}>
                  <input
                    type="checkbox"
                    checked={roleIds.includes(role.id)}
                    onChange={(event) =>
                      setRoleIds((current) =>
                        event.target.checked ? [...current, role.id] : current.filter((id) => id !== role.id),
                      )
                    }
                  />
                  {role.name}
                </label>
              ))}
            </fieldset>
          )}
          <button
            className="primaryAction"
            disabled={isSaving || !name.trim() || (typeCost === 'regular' && roleIds.length === 0)}
          >
            יצירת עלות
          </button>
        </form>
      </section>

      <section className="workforceBand">
        <h2>ערך חדש לפי תאריך תחולה</h2>
        <form className="workforceForm" onSubmit={onAddCostValue}>
          <label>
            עלות
            <select
              required
              value={valueCostId ?? ''}
              onChange={(event) => {
                setValueCostId(Number(event.target.value));
                setValueRoleId(null);
              }}
            >
              <option value="">בחירה</option>
              {data?.costs
                .filter((cost) => cost.isActive)
                .map((cost) => (
                  <option key={cost.id} value={cost.id}>
                    {cost.name}
                  </option>
                ))}
            </select>
          </label>
          {(selectedCost?.additionalCode === 'tip_grade' ||
            selectedCost?.additionalCode === 'minimum_completion') && (
            <label>
              תפקיד
              <select
                required
                value={valueRoleId ?? ''}
                onChange={(event) => setValueRoleId(Number(event.target.value))}
              >
                <option value="">בחירה</option>
                {data?.roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            ערך
            <input
              required
              inputMode="decimal"
              pattern="\d+(\.\d{1,2})?"
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </label>
          <label>
            מתאריך
            <input
              required
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </label>
          <label>
            עד תאריך
            <input
              type="date"
              min={startDate}
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </label>
          <button className="primaryAction" disabled={isSaving || !valueCostId || !value}>
            שמירת ערך
          </button>
        </form>
      </section>

      <section className="workforceBand">
        <h2>סיווג משמרות לחלוקת טיפים</h2>
        <div className="classificationGrid">
          {data?.shifts.map((shift) => (
            <label key={shift.id}>
              <span>{shift.name}</span>
              <select
                value={shift.tipShiftType ?? ''}
                onChange={(event) => void onChangeShiftTipType(shift.id, event.target.value)}
              >
                <option value="">ללא סיווג</option>
                <option value="morning">בוקר</option>
                <option value="evening">ערב</option>
              </select>
            </label>
          ))}
        </div>
      </section>

      <div className="reportTableWrap">
        <table role="table" className="reportTable costsTable">
          <caption>עלויות וערכים היסטוריים</caption>
          <thead>
            <tr>
              <th>עלות</th>
              <th>סוג</th>
              <th>תפקידים</th>
              <th>ערכים</th>
              <th>מצב</th>
            </tr>
          </thead>
          <tbody>
            {data?.costs.map((cost) => (
              <tr key={cost.id}>
                <td data-label="עלות">
                  <strong>{cost.name}</strong>
                </td>
                <td data-label="סוג">
                  {cost.additionalCode
                    ? additionalLabels[cost.additionalCode]
                    : cost.type
                      ? unitLabels[cost.type]
                      : '—'}
                </td>
                <td data-label="תפקידים">{cost.roles.map((role) => role.name).join(', ') || 'כללי'}</td>
                <td data-label="ערכים">
                  {cost.values.length
                    ? cost.values.map((item) => (
                        <div key={item.id} className={!item.isActive ? classes.inactiveValue : ''}>
                          {item.value} · {item.valueDateStart}
                          {item.endDateValue ? ` עד ${item.endDateValue}` : ''}
                          {item.rolePk
                            ? ` · ${data.roles.find((role) => role.id === item.rolePk)?.name ?? ''}`
                            : ''}
                        </div>
                      ))
                    : 'טרם הוגדר'}
                  {cost.valuesPagination?.hasMore && <button type="button" className="secondaryAction" disabled={isLoadingMore} onClick={() => void onLoadMoreValues(cost)}>היסטוריה נוספת</button>}
                </td>
                <td data-label="מצב">
                  <button
                    type="button"
                    className="secondaryAction"
                    disabled={isSaving}
                    onClick={() => void onToggleCostActive(cost.id, cost.isActive)}
                  >
                    {cost.isActive ? 'השבתה' : 'הפעלה'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data?.pagination?.hasMore && <button type="button" className="secondaryAction" disabled={isLoadingMore} onClick={() => void onLoadMoreCosts()}>טעינת עלויות נוספות</button>}
    </section>
  );
};
